import CommonModal from '@/components/customModal/commonModal/commonModal';
import { SettingsPanel } from '@/components/settings-panel';
import { addNode, addTemplate, useActions } from '@/node-editor/store/actions';
import { getNodes, useSelectors } from '@/node-editor/store/selectors';
import { mergeSavedTemplateData } from '@/node-editor/utilities';
import {
  fetchNodeTemplatesService,
  IStoredNodeTemplate,
} from '@/services/nodeTemplateService';
import { INode, INodeTemplate, TNodes } from '@/types/node';
import { Box } from '@mui/material';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { IAppenderDialogProps } from './interface/appender.interface';
import NodeByTag from './nodeByTag';
import TemplateList from './templateList';

const TAG_HELP_SECTION: Record<string, string> = {
  layout: 'pages.layout',
  content: 'pages.content',
  'setup & tools': 'pages.setupAndTools',
  interactivity: 'pages.interactivity',
};

export const AppenderDialog = forwardRef<any, IAppenderDialogProps>(
  ({ templates }, ref) => {
    const [node, setNode] = useState<INode | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(false);
    const [savedTemplates, setSavedTemplates] = useState<IStoredNodeTemplate[]>(
      []
    );
    const [allowedNodes, setAllowedNodes] = useState<string[]>([]);

    const privateSavedTemplates = savedTemplates.filter((st) => !st.public);
    const publicSavedTemplates = savedTemplates.filter((st) => st.public);

    const selectors = useSelectors(() => ({ getNodes }));
    const actions = useActions(() => ({ addNode, addTemplate }));
    const nodes = selectors.getNodes();

    const loadTemplates = async () => {
      const response = await fetchNodeTemplatesService();
      setSavedTemplates(response.data);
    };

    useImperativeHandle(ref, () => ({
      open: (id: string) => {
        setIsOpen(true);
        setNode(nodes[id]);
        loadTemplates();
      },
      close: () => setIsOpen(false),
    }));

    const handleSelection = (template: INodeTemplate, id: string) => {
      setIsOpen(false);
      // `justAdded` flows through createNode onto the new node and makes
      // NodeControls auto-open the settings dialog (it then strips the flag).
      actions.addNode(
        { ...template, justAdded: true } as INodeTemplate & {
          justAdded: boolean;
        },
        id
      );
    };

    // Inserts a saved/public template's content into the page currently being
    // edited, at the spot the "+" was clicked. Selection-only - renaming/
    // deleting/toggling public happens from the dedicated Manage Templates
    // view (client/app/managerConference/content/pages/templates), not here.
    const handleTemplate = (template: IStoredNodeTemplate) => {
      if (!node) return;
      setIsOpen(false);

      const order =
        node.type === 'root'
          ? Object.values(nodes).filter((n) => n.nodeId === node.id).length
          : (node.children?.length ?? 0);

      const merged = mergeSavedTemplateData(template.nodes?.nodes, node.id, order);
      actions.addTemplate((merged as unknown) as TNodes, node.id);
    };

    const tagMap = useCallback(
      () =>
        Object.values(templates)
          .filter(
            (n) => !(n as INodeTemplate & { disabled?: boolean }).disabled
          )
          .reduce((map, current) => {
            if (current.supports.appender && !map.includes(current.tag)) {
              map.push(current.tag);
            }
            return map;
          }, [] as string[]),
      [templates]
    );

    const enabled = useCallback(
      (type: string): boolean =>
        !allowedNodes || allowedNodes.length === 0 || allowedNodes.includes(type),
      [allowedNodes]
    );

    useEffect(() => {
      if (node !== undefined) {
        setAllowedNodes(templates[node.type]?.supports.children ?? []);
      }
      return () => setAllowedNodes([]);
    }, [node]);

    return (
      <CommonModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Component"
        maxWidth="md"
        fullWidth
      >
        {tagMap().map((tag, index) => (
          <SettingsPanel
            key={`${tag}-${index}`}
            label={tag.charAt(0).toUpperCase() + tag.slice(1)}
            sectionKey={TAG_HELP_SECTION[tag.toLowerCase()]}
            initialOpen
          >
            <NodeByTag
              tag={tag}
              templates={templates}
              enabled={enabled}
              onSelect={handleSelection}
              nodeId={node?.id ?? ''}
            />
          </SettingsPanel>
        ))}
        <SettingsPanel label="Your saved templates" initialOpen>
          {privateSavedTemplates.length === 0 ? (
            <Box textAlign="center">You have not saved any templates yet.</Box>
          ) : (
            <TemplateList
              savedList={privateSavedTemplates}
              templates={templates}
              enabled={enabled}
              onSelect={handleTemplate}
            />
          )}
        </SettingsPanel>
        <SettingsPanel label="Public templates" initialOpen>
          <TemplateList
            savedList={publicSavedTemplates}
            templates={templates}
            enabled={enabled}
            onSelect={handleTemplate}
          />
        </SettingsPanel>
      </CommonModal>
    );
  }
);

AppenderDialog.displayName = 'AppenderDialog';
